import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResidueType } from "../../types/residue.type";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  Switch,
  Tag,
} from "antd";
import { api } from "../../api/api";
import { DownOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { canEdit, hasAdminAccess } from "../../utils/roleUtils";
import { useAuth } from "../../contexts/auth.context";
import {useExchangeRate} from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { Search } = Input;

export default function Price() {
  const { authState } = useAuth();
  const { type } = useParams();
  const [data, setData] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ResidueType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCharacteristicsModalOpen, setIsCharacteristicsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [characteristicsForm] = Form.useForm();
  const [characteristicsLoading, setCharacteristicsLoading] = useState<boolean>(false);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [priceLoadingDetailed, setPriceLoadingDetailed] = useState(false);
  const [priceLoadingPretty, setPriceLoadingPretty] = useState(false);
  const {exchangeRate} = useExchangeRate();
  const [hasResidue, setHasResidue] = useState<boolean>(false);



  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/residue", {
        params: {
          page,
          type,
          limit: pageSize,
          search: searchTerm,
          hasResidue: hasResidue ? 'true' : undefined,
        },
      });

      const { data, total } = response.data;

      setData(data);
      setTotal(total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение остатка.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchTerm, type, hasResidue]);

  const handleOpenModal = (item: ResidueType) => {
    setSelectedItem(item);
    form.setFieldsValue({
      amount: item.price[item.price.currency],
      currency: item.price.currency,
      exchangeRate: item.price.exchangeRate,
      lowTradePrice: item.lowTradePrice
        ? item.lowTradePrice[item.price.currency]
        : undefined,
      upTradePrice: item.upTradePrice
        ? item.upTradePrice[item.price.currency]
        : undefined,
    });
    setIsModalOpen(true);
  };

  const handleOpenCharacteristicsModal = (item: ResidueType) => {
    setSelectedItem(item);
    characteristicsForm.setFieldsValue({
      characteristics: item.characteristics && item.characteristics.length > 0
        ? item.characteristics
        : [{ key: "", value: "" }],
    });
    setIsCharacteristicsModalOpen(true);
  };

  const handleExport = () => {
    downloadFile("/export/residues", "residue_data.xlsx", { type });
  };

  const handleGetPriceDetailed = async () => {
    setPriceLoadingDetailed(true);
    try {
      await downloadFile("/price/list", `price_list_${type}.xlsx`, { 
        type,
        hasResidue: hasResidue ? 'true' : undefined,
      });
    } finally {
      setPriceLoadingDetailed(false);
    }
  };

  const handleGetPricePretty = async () => {
    setPriceLoadingPretty(true);
    try {
      await downloadFile("/price/list-pretty", `price_pretty_${type}.pdf`, { 
        type,
        hasResidue: hasResidue ? 'true' : undefined,
      });
    } finally {
      setPriceLoadingPretty(false);
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
      width: 200,
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      width: 100,
      render: (_: any, record: ResidueType) => {
        return record.residue !== undefined
          ? `${roundToThreeDecimals(record.residue)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена мин USD",
      key: "lowTradePriceUSD",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.lowTradePrice && record.lowTradePrice.USD !== undefined
          ? `${roundToThreeDecimals(record.lowTradePrice.USD)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена мин TJS",
      key: "lowTradePriceTJS",
      width: 120,
      render: (_: any, record: ResidueType) => {
        return record.lowTradePrice && record.lowTradePrice.TJS !== undefined
          ? `${roundToThreeDecimals(record.lowTradePrice.TJS)}`
          : "Н/Д";
      },
    },
    {
      title: "Характеристики",
      key: "characteristics",
      width: 250,
      render: (_: any, record: ResidueType) => {
        if (!record.characteristics || record.characteristics.length === 0) {
          return <span style={{ color: "#999" }}>Нет характеристик</span>;
        }
        return (
          <Space wrap>
            {record.characteristics.map((char, index) => (
              <Tag key={index} color="blue">
                {char.key}: {char.value}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 150,
      fixed: "right" as "right",
      render: (_: any, record: ResidueType) => {
        const menu = (
          <Menu>
            {canEdit(authState.user?.role) && (
              <>
              <Menu.Item onClick={() => handleOpenModal(record)}>
                  Изменить цену
                </Menu.Item>
                <Menu.Item onClick={() => handleOpenCharacteristicsModal(record)}>
                  Изменить характеристики
              </Menu.Item>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };


  const handleSubmitForm = async (values: {
    currency: Currency;
    amount: number;
    lowTradePrice?: number;
    upTradePrice?: number;
  }) => {
    setConfirmLoading(true);

    const data: any = {
      currency: values.currency,
      amount: values.amount,
      type,
      exchangeRate: exchangeRate
    };

    // Передаем lowTradePrice только если оно указано, иначе null для удаления
    if (values.lowTradePrice !== undefined && values.lowTradePrice !== null) {
      data.lowTradePrice = values.lowTradePrice;
    } else {
      data.lowTradePrice = null;
    }

    // Передаем upTradePrice только если оно указано, иначе null для удаления
    if (values.upTradePrice !== undefined && values.upTradePrice !== null) {
      data.upTradePrice = values.upTradePrice;
    } else {
      data.upTradePrice = null;
    }
    
    try {
      await api.post(`/price/${selectedItem?._id}`, data);

      message.success("Цены обновлены");
      form.resetFields();
      fetchData();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving price:", error);
      message.error(error.response.data.error);
    }
  };

  const handleSubmitCharacteristics = async (values: {
    characteristics: Array<{ key: string; value: string }>;
  }) => {
    setCharacteristicsLoading(true);

    // Фильтруем пустые характеристики
    const filteredCharacteristics = values.characteristics.filter(
      (char) => char.key.trim() !== "" && char.value.trim() !== ""
    );

    const data = {
      type,
      characteristics: filteredCharacteristics,
    };

    try {
      await api.post(`/price/${selectedItem?._id}/characteristics`, data);
      message.success("Характеристики обновлены");
      characteristicsForm.resetFields();
      fetchData();
      setIsCharacteristicsModalOpen(false);
      setCharacteristicsLoading(false);
    } catch (error: any) {
      setCharacteristicsLoading(false);
      console.error("Error saving characteristics:", error);
      message.error(error.response?.data?.error || "Ошибка при сохранении характеристик");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Прайсы</Title>
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
          style={{ width: 300 }}
        />

        <Space>
          <span>Имеет остаток:</span>
          <Switch
            checked={hasResidue}
            onChange={setHasResidue}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Space>

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          loading={priceLoadingPretty}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleGetPricePretty}
        >
          Прайс
        </Button>
        {hasAdminAccess(authState.user?.role) && (
          <Button
            type="default"
            loading={priceLoadingDetailed}
            icon={<PiMicrosoftExcelLogoFill />}
            onClick={handleGetPriceDetailed}
          >
            Прайс (подробный)
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить цену"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select placeholder="Выберите валюту">
              {Object.values(Currency).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="lowTradePrice"
            label="Цена мин (в выбранной валюте)"
            rules={[{ required: false }]}
          >
            <InputNumber min={0} placeholder="Введите минимальную цену" />
          </Form.Item>

          <Form.Item
            name="upTradePrice"
            label="Цена торговли до"
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить характеристики"
        open={isCharacteristicsModalOpen}
        onCancel={() => {
          setIsCharacteristicsModalOpen(false);
          setSelectedItem(null);
        }}
        onOk={() => characteristicsForm.submit()}
        confirmLoading={characteristicsLoading}
        width={600}
      >
        <Form
          form={characteristicsForm}
          onFinish={handleSubmitCharacteristics}
          layout="vertical"
        >
          <Form.List name="characteristics">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, "key"]}
                      rules={[{ required: true, message: "Введите ключ" }]}
                      style={{ flex: 1 }}
                    >
                      <Input placeholder="Ключ характеристики" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "value"]}
                      rules={[{ required: true, message: "Введите значение" }]}
                      style={{ flex: 1 }}
                    >
                      <Input placeholder="Значение характеристики" />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button type="link" danger onClick={() => remove(name)}>
                        Удалить
                      </Button>
                    )}
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + Добавить характеристику
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
